// My Camps Page
// Main page for viewing and managing extracted camp email data

import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import Icon from '@/components/ui/Icon';
import AIDiscoveryIntroCard from '@/components/ai-discovery/AIDiscoveryIntroCard';
import { getCampEmailStats, getCampEmails, updateCampStatus } from '@/services/campEmailService';
import { useEmailConnection } from '@/hooks/email/useEmailConnection';
import { useAuth } from '@/contexts/AuthContext';
import { User, UserProgram } from '@/api/entities';
import DashboardEmailDiscoverySection from '@/components/DashboardEmailDiscoverySection';
import { toast } from '@/components/ui/use-toast';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';

// Import new dashboard component
import MyCampsDashboard from '@/components/dashboard/MyCampsDashboard';

// Lazy load ExtractedCamps to improve initial page load
const ExtractedCamps = lazy(() => import('@/components/camps/ExtractedCamps'));

/**
 * My Camps Page
 * Main interface for viewing and managing extracted camp email data
 */
const MyCamps = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recentCamps, setRecentCamps] = useState([]);
  const [userProfile, setUserProfile] = useState(null); // Full user profile with children
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [discoveredCamps, setDiscoveredCamps] = useState([]);
  const [discoveredCampStats, setDiscoveredCampStats] = useState({
    total: 0,
    pending: 0,
    favorite: 0,
    hidden: 0
  });
  const [pendingActionCampId, setPendingActionCampId] = useState(null);

  // Email connection hook
  const {
    isConnected: isEmailConnected,
    syncStatus: emailSyncStatus,
    error: emailError
  } = useEmailConnection();

  // Handle email connection for AI Discovery
  const handleConnectEmail = () => {
    // Navigate to profile page email connection section
    window.location.href = '/profile#email_connected';
  };

  // Handle navigation to Discovery page
  const handleDiscover = () => {
    navigate('/Discovery');
  };

  const filterDiscoveredCamps = useCallback((camps) => {
    return (camps || []).filter((camp) => {
      const name = camp.camp_name || camp.email_subject || camp.name;
      if (!name || !String(name).trim()) return false;
      const status = camp.user_status;
      return !status || status === 'new' || status === 'pending';
    });
  }, []);

  const refreshCampLists = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [campStats, camps] = await Promise.all([
        getCampEmailStats(user.id),
        getCampEmails(user.id)
      ]);

      const normalizedCamps = camps || [];
      setStats(campStats || {});
      setRecentCamps(normalizedCamps);

      const filtered = filterDiscoveredCamps(normalizedCamps);
      setDiscoveredCamps(filtered);
      setDiscoveredCampStats({
        total: filtered.length,
        pending: campStats?.new || 0,
        favorite: campStats?.favorited || 0,
        hidden: campStats?.hidden || 0
      });
    } catch (err) {
      console.error('Error refreshing camp lists:', err);
      toast({
        title: 'Unable to refresh camps',
        description: 'Please try again in a moment.',
        variant: 'destructive'
      });
    }
  }, [filterDiscoveredCamps, toast, user?.id]);

  const updateCampStatusAndRefresh = useCallback(
    async (campId, status, successMessage, { removeFromDiscovered = false } = {}) => {
      if (!campId || !user?.id) return;
      if (pendingActionCampId && pendingActionCampId !== campId) {
        // Prevent overlapping actions; allow same card double-click to be ignored during pending state
        return;
      }

      try {
        setPendingActionCampId(campId);
        await updateCampStatus(campId, status);

        if (removeFromDiscovered) {
          setDiscoveredCamps((prev) => prev.filter((camp) => camp.id !== campId));
        }

        if (successMessage) {
          toast({
            title: successMessage
          });
        }

        await refreshCampLists();
      } catch (err) {
        console.error('Error updating camp status:', err);
        toast({
          title: 'Unable to update camp',
          description: 'Please try again.',
          variant: 'destructive'
        });
      } finally {
        setPendingActionCampId(null);
      }
    },
    [pendingActionCampId, refreshCampLists, toast, user?.id]
  );

  const handleFavoriteDiscoveredCamp = useCallback(
    async (campId) => {
      await updateCampStatusAndRefresh(campId, 'favorited', 'Camp added to favorites', { removeFromDiscovered: true });
    },
    [updateCampStatusAndRefresh]
  );

  const handleSaveDiscoveredCamp = useCallback(
    async (campId) => {
      await updateCampStatusAndRefresh(campId, 'saved_for_later', 'Camp saved to My Camps', { removeFromDiscovered: true });
    },
    [updateCampStatusAndRefresh]
  );

  const handleDeleteDiscoveredCamp = useCallback(
    async (campId) => {
      await updateCampStatusAndRefresh(campId, 'hidden', 'Camp removed from AI discoveries', { removeFromDiscovered: true });
    },
    [updateCampStatusAndRefresh]
  );

  const handleSaveDiscoveredCampDetails = useCallback(
    async (formData, campId) => {
      try {
        if (campId) {
          await UserProgram.update(campId, formData);
        } else {
          await UserProgram.create({
            ...formData,
            user_id: user?.id,
            source: 'ai_discovered'
          });
        }

        await refreshCampLists();
      } catch (err) {
        console.error('Error saving discovered camp details:', err);
        throw err;
      }
    },
    [refreshCampLists, user?.id]
  );

  const handleSavedFavoriteCamp = useCallback(
    async (camp) => {
      await updateCampStatusAndRefresh(camp?.id, 'favorited', 'Marked as favorite');
    },
    [updateCampStatusAndRefresh]
  );

  const handleSavedSaveForLater = useCallback(
    async (camp) => {
      await updateCampStatusAndRefresh(camp?.id, 'saved_for_later', 'Saved for later');
    },
    [updateCampStatusAndRefresh]
  );

  const handleSavedDeleteCamp = useCallback(
    async (camp) => {
      await updateCampStatusAndRefresh(camp?.id, 'hidden', 'Removed from My Camps');
    },
    [updateCampStatusAndRefresh]
  );

  /**
   * Load user and camp data
   */
  const loadData = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const currentUser = await User.me().catch(() => null);
      setUserProfile(currentUser);
      await refreshCampLists();
    } catch (err) {
      console.error('Error loading camp data:', err);
      setError('Failed to load camp data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert extracted camps to the format expected by MyCampsDashboard
  // IMPORTANT: This useMemo must be called BEFORE any early returns to follow Rules of Hooks
  const convertCampsToDashboardFormat = useMemo(() => {
    return recentCamps.map(camp => ({
      id: camp.id,
      name: camp.camp_name || camp.email_subject || 'Unnamed Camp',
      organization: camp.camp_organization || camp.sender_name || 'Unknown Organization',
      location: camp.camp_location || 'Unknown Location',
      ageRange: camp.age_range || 'All Ages',
      status: camp.needs_manual_review ? ['considering'] : ['saved'],
      campStartDate: camp.camp_start_date ? new Date(camp.camp_start_date) : null,
      campEndDate: camp.camp_end_date ? new Date(camp.camp_end_date) : null,
      registrationDeadline: camp.registration_deadline ? new Date(camp.registration_deadline) : null,
      earlyBirdDeadline: camp.early_bird_deadline ? new Date(camp.early_bird_deadline) : null,
      regularPrice: camp.cost_structure,
      earlyBirdPrice: camp.early_bird_price,
      website: camp.website,
      phone: camp.phone,
      email: camp.email,
      assignedChildren: [],
      refundPolicy: camp.refund_policy,
      notes: camp.notes,
      source: 'ai_discovered',
      yearTracked: new Date().getFullYear(),
      recommendedBy: null,
      // Additional fields for compatibility
      address: camp.camp_location || 'Unknown Location',
      price: camp.cost_structure,
      age_min: camp.age_min,
      age_max: camp.age_max,
      image_url: camp.image_url,
      latitude: camp.latitude,
      longitude: camp.longitude,
      category: 'camp',
      // Store original camp data for compatibility
      originalCamp: camp
    }));
  }, [recentCamps]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <AppLoadingSpinner message="Loading your camps..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Camps</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* AI Discovery Intro Card - Replace banner */}
      {!isEmailConnected && discoveredCamps.length === 0 && (
        <AIDiscoveryIntroCard 
          onConnectEmail={handleConnectEmail}
          isEmailConnected={isEmailConnected}
          discoveredCampsCount={discoveredCampStats?.total || 0}
          messagesCount={0}
          variant="mycamps" 
        />
      )}

      <DashboardEmailDiscoverySection 
        discoveredCamps={discoveredCamps}
        totalDiscovered={discoveredCampStats.total}
        isEmailConnected={isEmailConnected}
        onViewAllDiscoveries={() => {
          window.location.href = '/email-scout-details';
        }}
        onFavoriteCamp={handleFavoriteDiscoveredCamp}
        onSaveForLater={handleSaveDiscoveredCamp}
        onDeleteCamp={handleDeleteDiscoveredCamp}
        onConnectEmail={handleConnectEmail}
        onSaveCamp={handleSaveDiscoveredCampDetails}
      />

      {/* Use the new dashboard component */}
      <MyCampsDashboard 
        camps={convertCampsToDashboardFormat}
        children={userProfile?.children || []} // Pass user's children data for photo display
        onAddCamp={() => {
          // Could redirect to discovery page or show add camp modal
          window.location.href = '/Discovery';
        }}
        onDiscover={handleDiscover}
        onEditCamp={(camp) => {
          // Handle editing extracted camps
          console.log('Edit camp:', camp);
        }}
        onRemoveCamp={(camp) => {
          // Handle removing camps from extracted list
          console.log('Remove camp:', camp);
        }}
        onBookCamp={(camp) => {
          // Handle booking camps
          console.log('Book camp:', camp);
        }}
        onWhosIn={(camp) => {
          console.log('Who\'s in for camp:', camp);
        }}
        onCancelBooking={(camp) => {
          console.log('Cancel booking for camp:', camp);
        }}
        onMarkReviewed={(camp) => {
          console.log('Mark as reviewed:', camp);
        }}
        onFavoriteCamp={handleSavedFavoriteCamp}
        onSaveForLater={handleSavedSaveForLater}
        onDeleteCamp={handleSavedDeleteCamp}
        isEmailConnected={isEmailConnected}
        emailSyncStatus={emailSyncStatus}
        emailError={emailError}
        stats={stats}
      />
    </div>
  );
};

export default MyCamps;
